import { useCallback, useEffect, useMemo, useState } from 'react';
import { Title } from 'react-admin';

import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
    getPracticeTrends,
    type PracticeTrendCategory,
    type PracticeTrendCounts,
    type PracticeTrendExamName,
    type PracticeTrendTaskType,
    type PracticeTrendsData,
} from '../admin/adminPracticeTrendsApi';

const examNames: PracticeTrendExamName[] = ['Academic', 'Core'];
const categories: PracticeTrendCategory[] = ['Speaking', 'Writing', 'Reading', 'Listening'];
const windowOptions = [
    { label: 'Last 1 minute', shortLabel: '1m', minutes: 1 },
    { label: 'Last 2 minutes', shortLabel: '2m', minutes: 2 },
    { label: 'Last 3 minutes', shortLabel: '3m', minutes: 3 },
    { label: 'Last 4 minutes', shortLabel: '4m', minutes: 4 },
    { label: 'Last 5 minutes', shortLabel: '5m', minutes: 5 },
    { label: 'Last 10 minutes', shortLabel: '10m', minutes: 10 },
    { label: 'Last 20 minutes', shortLabel: '20m', minutes: 20 },
    { label: 'Last 30 minutes', shortLabel: '30m', minutes: 30 },
    { label: 'Last 1 hour', shortLabel: '1h', minutes: 60 },
    { label: 'Last 2 hours', shortLabel: '2h', minutes: 120 },
    { label: 'Last 5 hours', shortLabel: '5h', minutes: 300 },
    { label: 'Last 10 hours', shortLabel: '10h', minutes: 600 },
    { label: 'Last 12 hours', shortLabel: '12h', minutes: 720 },
    { label: 'Last 1 day', shortLabel: '1d', minutes: 1440 },
    { label: 'Last 2 days', shortLabel: '2d', minutes: 2880 },
    { label: 'Last 7 days', shortLabel: '7d', minutes: 10080 },
];

type ScoreColumn = {
    key: keyof PracticeTrendCounts;
    label: string;
};

const scoreColumns: ScoreColumn[] = [
    { key: 'zeroScoreUsers', label: 'Zero' },
    { key: 'above20Users', label: '20+' },
    { key: 'above40Users', label: '40+' },
    { key: 'above50Users', label: '50+' },
    { key: 'above60Users', label: '60+' },
    { key: 'above70Users', label: '70+' },
    { key: 'above80Users', label: '80+' },
];

function toUtcIso(date: Date) {
    return date.toISOString();
}

function formatDateTime(value: string) {
    if (!value) return '';
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(value));
}

function getTotalUsers(data: PracticeTrendsData | null) {
    if (!data) return 0;
    return data.taskTypes.reduce((total, taskType) => total + taskType.totals.practicedUsers, 0);
}

function SummaryTile({ label, value }: { label: string; value: number | string }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 1,
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1.25, sm: 1.5 },
                minWidth: 0,
                width: '100%',
                backgroundColor: '#ffffff',
            }}
        >
            <Typography sx={{ color: '#64748b', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase' }}>
                {label}
            </Typography>
            <Typography sx={{ mt: 0.5, color: '#0f172a', fontSize: '1.35rem', fontWeight: 800 }}>
                {value}
            </Typography>
        </Paper>
    );
}

function CountPill({ label, value, danger = false }: { label: string; value: number; danger?: boolean }) {
    return (
        <Box
            sx={{
                minWidth: 0,
                borderRadius: 1,
                px: 1,
                py: 0.85,
                backgroundColor: danger && value > 0 ? alpha('#ef4444', 0.08) : '#f8fafc',
                border: `1px solid ${danger && value > 0 ? alpha('#ef4444', 0.18) : '#e2e8f0'}`,
            }}
        >
            <Typography sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase' }}>
                {label}
            </Typography>
            <Typography sx={{ mt: 0.25, color: '#0f172a', fontSize: '1rem', fontWeight: 800 }}>
                {value}
            </Typography>
        </Box>
    );
}

function MobileTaskCard({ taskType }: { taskType: PracticeTrendTaskType }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 1,
                p: 1.5,
                backgroundColor: '#ffffff',
            }}
        >
            <Typography sx={{ color: '#0f172a', fontWeight: 800, lineHeight: 1.25 }}>
                {taskType.displayName}
            </Typography>
            <Typography sx={{ mt: 0.25, color: '#64748b', fontSize: '0.76rem' }}>
                {taskType.examTaskType}
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 0.75,
                    mt: 1.25,
                }}
            >
                <CountPill label="Attempts" value={taskType.totals.attemptCount} />
                <CountPill label="Users" value={taskType.totals.practicedUsers} />
            </Box>

            <Divider sx={{ my: 1.25 }} />

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                    gap: 0.75,
                }}
            >
                {scoreColumns.map((column) => (
                    <CountPill
                        key={column.key}
                        label={column.label}
                        value={taskType.totals[column.key]}
                        danger={column.key === 'zeroScoreUsers'}
                    />
                ))}
            </Box>
        </Paper>
    );
}

export function PracticeTrendsPage() {
    const [examName, setExamName] = useState<PracticeTrendExamName>('Academic');
    const [category, setCategory] = useState<PracticeTrendCategory>('Speaking');
    const [windowMinutes, setWindowMinutes] = useState(30);
    const [data, setData] = useState<PracticeTrendsData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const queryWindow = useMemo(() => {
        const to = new Date();
        const from = new Date(to.getTime() - windowMinutes * 60 * 1000);
        return {
            fromUtc: toUtcIso(from),
            toUtc: toUtcIso(to),
        };
    }, [windowMinutes]);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await getPracticeTrends({
                examName,
                category,
                fromUtc: queryWindow.fromUtc,
                toUtc: queryWindow.toUtc,
                intervalMinutes: windowMinutes,
            });
            setData(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Practice trends failed');
        } finally {
            setIsLoading(false);
        }
    }, [category, examName, queryWindow.fromUtc, queryWindow.toUtc, windowMinutes]);

    useEffect(() => {
        void load();
    }, [load]);

    const totalAttempts = data?.taskTypes.reduce((total, taskType) => total + taskType.totals.attemptCount, 0) ?? 0;
    const totalUsers = getTotalUsers(data);

    return (
        <Box sx={{ p: { xs: 1.25, sm: 2, md: 3 }, backgroundColor: '#f8fafc', minHeight: '100%', minWidth: 0 }}>
            <Title title="Practice Trends" />

            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                spacing={2}
                sx={{ mb: 2 }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            color: '#0f172a',
                            fontWeight: 800,
                            letterSpacing: 0,
                            fontSize: { xs: '1.45rem', sm: '2rem', md: '2.125rem' },
                            lineHeight: 1.15,
                        }}
                    >
                        Practice Trends
                    </Typography>
                    <Typography sx={{ mt: 0.5, color: '#64748b', fontSize: '0.92rem' }}>
                        {data ? `${formatDateTime(data.fromUtc)} - ${formatDateTime(data.toUtc)}` : 'Loading'}
                    </Typography>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Box
                        sx={{
                            maxWidth: { xs: '100%', md: 'min(64vw, 850px)' },
                            overflowX: 'auto',
                            pb: 0.25,
                        }}
                    >
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={windowMinutes}
                            onChange={(_, value: number | null) => {
                                if (value !== null) setWindowMinutes(value);
                            }}
                            aria-label="Practice trends window"
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {windowOptions.map((option) => (
                                <ToggleButton
                                    key={option.minutes}
                                    value={option.minutes}
                                    aria-label={option.label}
                                    title={option.label}
                                    sx={{ px: 1.25, fontWeight: 700, textTransform: 'none' }}
                                >
                                    {option.shortLabel}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                        onClick={() => void load()}
                        disabled={isLoading}
                        fullWidth={false}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                        Refresh
                    </Button>
                </Stack>
            </Stack>

            <Paper variant="outlined" sx={{ borderRadius: 1, mb: 2, overflow: 'hidden', minWidth: 0 }}>
                <Tabs
                    value={examName}
                    onChange={(_, value: PracticeTrendExamName) => setExamName(value)}
                    variant="fullWidth"
                    sx={{ px: { xs: 0, sm: 1 }, borderBottom: '1px solid #e2e8f0', minHeight: 44 }}
                >
                    {examNames.map((name) => (
                        <Tab key={name} label={name} value={name} sx={{ minHeight: 44, fontSize: { xs: '0.78rem', sm: '0.875rem' } }} />
                    ))}
                </Tabs>
                <Tabs
                    value={category}
                    onChange={(_, value: PracticeTrendCategory) => setCategory(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{ px: { xs: 0, sm: 1 }, minHeight: 44 }}
                >
                    {categories.map((item) => (
                        <Tab key={item} label={item} value={item} sx={{ minHeight: 44, minWidth: { xs: 92, sm: 120 }, fontSize: { xs: '0.78rem', sm: '0.875rem' } }} />
                    ))}
                </Tabs>
            </Paper>

            {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 1.5,
                    mb: 2,
                }}
            >
                <SummaryTile label="Attempts" value={totalAttempts} />
                <SummaryTile label="Practice Users" value={totalUsers} />
            </Box>

            <Stack spacing={1.25} sx={{ display: { xs: 'flex', md: 'none' } }}>
                {data?.taskTypes.map((taskType) => (
                    <MobileTaskCard key={taskType.examTaskType} taskType={taskType} />
                ))}
                {!isLoading && data?.taskTypes.length === 0 ? (
                    <Paper variant="outlined" sx={{ borderRadius: 1, p: 3, textAlign: 'center' }}>
                        <Typography sx={{ color: '#64748b' }}>No task types</Typography>
                    </Paper>
                ) : null}
            </Stack>

            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    display: { xs: 'none', md: 'block' },
                    borderRadius: 1,
                    maxHeight: 'calc(100vh - 310px)',
                    backgroundColor: '#ffffff',
                    width: '100%',
                    overflowX: 'auto',
                }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ minWidth: 220, fontWeight: 800 }}>Task Type</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>Attempts</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>Users</TableCell>
                            {scoreColumns.map((column) => (
                                <TableCell key={column.key} align="right" sx={{ fontWeight: 800 }}>
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.taskTypes.map((taskType) => (
                            <TableRow key={taskType.examTaskType} hover>
                                <TableCell>
                                    <Typography sx={{ color: '#0f172a', fontWeight: 750 }}>
                                        {taskType.displayName}
                                    </Typography>
                                    <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                                        {taskType.examTaskType}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">{taskType.totals.attemptCount}</TableCell>
                                <TableCell align="right">{taskType.totals.practicedUsers}</TableCell>
                                {scoreColumns.map((column) => (
                                    <TableCell
                                        key={column.key}
                                        align="right"
                                        sx={{
                                            backgroundColor:
                                                column.key === 'zeroScoreUsers' && taskType.totals[column.key] > 0
                                                    ? alpha('#ef4444', 0.08)
                                                    : 'inherit',
                                        }}
                                    >
                                        {taskType.totals[column.key]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        {!isLoading && data?.taskTypes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11}>
                                    <Typography sx={{ py: 4, textAlign: 'center', color: '#64748b' }}>
                                        No task types
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : null}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
